/**
 * API Query Hook using TanStack Query + fetch
 * Facade pattern for React SDK
 */

import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { ApiClient } from '../../core/resources/apiClient';

// Global API client instance
let globalApiClient: ApiClient | null = null;

export function setGlobalApiClient(client: ApiClient) {
    globalApiClient = client;
}

export function getGlobalApiClient(): ApiClient {
    if (!globalApiClient) {
        throw new Error('API client not initialized. Make sure TagadaProvider is wrapping your app.');
    }
    return globalApiClient;
}

export function getGlobalApiClientOrNull(): ApiClient | null {
    return globalApiClient;
}

// Query key builders for consistency
export const queryKeys = {
    checkout: (token: string, currency?: string) => ['checkout', token, currency].filter(Boolean),
    promotions: (sessionId: string) => ['promotions', sessionId],
    discounts: (sessionId: string) => ['discounts', sessionId],
    orderBumps: (sessionId: string) => ['order-bumps', sessionId],
    shippingRates: (sessionId: string) => ['shipping-rates', sessionId],
    vipPreview: (sessionId: string, offerIds: string[]) => ['vip-preview', sessionId, offerIds],
    products: (productIds: string[], options?: Record<string, any>) => ['products', productIds, options],
    store: (storeId: string) => ['store', storeId],
    storeConfig: (storeId: string) => ['storeConfig', storeId],
    order: (orderId: string) => ['order', orderId],
} as const;

// Main query hook
export function useApiQuery<T = unknown>(
    key: readonly unknown[],
    url: string | null,
    options?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>
) {
    const client = getGlobalApiClient();

    return useQuery<T>({
        queryKey: key,
        queryFn: async () => {
            if (!url) throw new Error('URL is required');
            return client.get<T>(url);
        },
        enabled: !!url && (options?.enabled !== false),
        staleTime: 30_000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime in v4)
        ...options,
    });
}

// Mutation hook
export function useApiMutation<TData = unknown, TVariables = unknown>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    options?: UseMutationOptions<TData, Error, TVariables>
) {
    return useMutation({
        mutationFn,
        ...options,
    });
}

// Convenience hooks for common operations
export function useInvalidateQuery() {
    const queryClient = useQueryClient();

    return {
        invalidateCheckout: (token: string) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.checkout(token) });
        },
        invalidatePromotions: (sessionId: string) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.promotions(sessionId) });
        },
        invalidateOrderBumps: (sessionId: string) => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.orderBumps(sessionId) });
        },
        invalidateAll: () => {
            void queryClient.invalidateQueries();
        },
    };
}

// Preload function for optimistic loading
export function usePreloadQuery() {
    const queryClient = useQueryClient();
    const client = getGlobalApiClient();

    return {
        preloadCheckout: (token: string, currency?: string) => {
            const url = `/api/v1/checkout-sessions/${token}/v2${currency ? `?currency=${currency}` : ''}`;
            void queryClient.prefetchQuery({
                queryKey: queryKeys.checkout(token, currency),
                queryFn: () => client.get(url),
                staleTime: 30_000,
            });
        },
    };
}
